import React, {useEffect, useState, useRef, useCallback, useContext} from 'react';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { t } from "i18next";
import i18n from '../i18n';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import '../css/postPage.css';
import '../css/postComments.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faUser } from "@fortawesome/free-regular-svg-icons";
import { faHeart, faShareAlt, faTrash, faComment, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button, Modal } from "react-bootstrap";
import LoadingSpinner from "../components/LoadingSpinner";
import PostComment from "../components/PostComment";
import { formatDate } from "../utils/dateUtils";
import api from "../api";
import {AuthContext} from "../context/AuthContext";
import DOMPurify from "dompurify";

function PostsPage() {
    const [posts, setPosts] = useState([]);
    const [bestPost, setBestPost] = useState(null);
    const [likedPosts, setLikedPosts] = useState(new Set());
    const [page, setPage] = useState(1);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    const lastPostElementRef = useRef();
    const [waitingCount, setWaitingCount] = useState(null);
    const [isReadyToShowButtons, setIsReadyToShowButtons] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [openComments, setOpenComments] = useState({});
    const accessToken = localStorage.getItem('access_token');
    const { isAuthenticated, isAdmin, isBanned, currentUserId } = useContext(AuthContext);

    useEffect(() => {
        loadBestPost();
    }, []);

    useEffect(() => {
        if (isAuthenticated && currentUserId !== null) {
            api.get(`${process.env.REACT_APP_API_URL}/api/posts/approvalCount/${currentUserId}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            })
                .then(response => {
                    setWaitingCount(response.data.count);
                })
                .catch(error => console.error('Error fetching approval count', error));
        }
    }, [isAuthenticated, currentUserId]);

    useEffect(() => {
        if (bestPost !== null) {
            setPosts([]);
            setPage(1);
            loadPosts(1, bestPost ? bestPost.id : null);
        }
    }, [bestPost]);

    useEffect(() => {
        const handleLanguageChange = () => {
            setPosts(prevPosts => [...prevPosts]);
        };
        i18n.on('languageChanged', handleLanguageChange);
        return () => i18n.off('languageChanged', handleLanguageChange);
    }, []);

    const onCommentCountUpdate = useCallback((postId, newCount) => {
        setPosts(prevPosts => {
            const currentPost = prevPosts.find(p => p.id === postId);
            if (currentPost && currentPost.comments !== newCount) {
                return prevPosts.map(p =>
                    p.id === postId ? { ...p, comments: newCount } : p
                );
            }
            return prevPosts;
        });
        if (bestPost && bestPost.id === postId && bestPost.comments !== newCount) {
            setBestPost(prevBestPost => ({ ...prevBestPost, comments: newCount }));
        }
    }, [bestPost]);

    const loadBestPost = () => {
        api.get(`${process.env.REACT_APP_API_URL}/api/posts/best-of-week`)
            .then(response => {
                if (response.data && !response.data.error) {
                    setBestPost(response.data);
                } else {
                    setBestPost(false);
                }
            })
            .catch(error => {
                setBestPost(false);
            });
    };

    const loadPosts = (page, bestPostId) => {
        if (loadingPosts || !hasMore) return;

        setLoadingPosts(true);
        setLoadingPage(true);
        api.get(`${process.env.REACT_APP_API_URL}/api/posts?page=${page}&limit=10`)
            .then(response => {
                if (response.data.error === 'No posts found') {
                    setHasMore(false);
                } else if (Array.isArray(response.data)) {
                    const approvedPosts = response.data.filter(post => post && post.status === 'approved');
                    const filteredPosts = bestPostId
                        ? approvedPosts.filter(post => post && post.id !== bestPostId)
                        : approvedPosts;

                    setPosts(prevPosts => [...prevPosts, ...filteredPosts]);
                    if (approvedPosts.length < 10) {
                        setHasMore(false);
                    }
                } else {
                    setHasMore(false);
                }
            })
            .catch(error => {
                setHasMore(false);
            })
            .finally(() => {
                setLoadingPosts(false);
                setLoadingPage(false);
            });
    };

    const handleLike = (postId) => {
        if (!accessToken) return;
        if (likedPosts.has(postId)) {
            api.delete(`${process.env.REACT_APP_API_URL}/api/post/${postId}/like`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            })
                .then(() => {
                    setLikedPosts((prev) => {
                        const newLikedPosts = new Set(prev);
                        newLikedPosts.delete(postId);
                        return newLikedPosts;
                    });
                    setPosts(prevPosts =>
                        prevPosts.map(post =>
                            post.id === postId ? { ...post, likes: post.likes - 1 } : post
                        )
                    );
                    if (bestPost && bestPost.id === postId) {
                        setBestPost(prevBestPost => ({ ...prevBestPost, likes: prevBestPost.likes - 1 }));
                    }
                })
                .catch(error => console.error('Error removing like', error));
        } else {
            api.post(`${process.env.REACT_APP_API_URL}/api/post/${postId}/like`, {}, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            })
                .then(() => {
                    setLikedPosts((prev) => {
                        const newLikedPosts = new Set(prev);
                        newLikedPosts.add(postId);
                        return newLikedPosts;
                    });
                    setPosts(prevPosts =>
                        prevPosts.map(post =>
                            post.id === postId ? { ...post, likes: post.likes + 1 } : post
                        )
                    );
                    if (bestPost && bestPost.id === postId) {
                        setBestPost(prevBestPost => ({ ...prevBestPost, likes: prevBestPost.likes + 1 }));
                    }
                })
                .catch(() => {
                    Swal.fire({
                        text: t('posts.alreadyLiked'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('posts.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                });
        }
    };

    const handleDeletePost = (postId) => {
        api.delete(`${process.env.REACT_APP_API_URL}/api/posts/${postId}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        })
            .then(() => {
                setPosts(posts.filter(post => post.id !== postId));
                if (bestPost && bestPost.id === postId) {
                    setBestPost(null);
                }
            })
            .catch(error => console.error('Error deleting post', error));
    };

    const handleShare = (postId) => {
        const shareUrl = `${window.location.origin}/posts/${postId}`;
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                Swal.fire({
                    text: t('posts.linkCopied'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch(error => console.error('Error copying link', error));
    };

    const confirmDeletePost = (postId) => {
        Swal.fire({
            text: t('admin.confirmDeletePost'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeletePost(postId);
            }
        });
    };

    const handleLogin = () => {
        const redirectPath = window.location.pathname;
        setShowModal(true);
        localStorage.setItem('redirectPath', redirectPath);
    };

    const confirmLogin = () => {
        const redirectPath = localStorage.getItem('redirectPath') || '/';
        window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/steam?redirect=${encodeURIComponent(redirectPath)}`;
        setShowModal(false);
    };

    useEffect(() => {
        if (loadingPosts) return;

        const handleObserver = entries => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore && !loadingPosts) {
                setPage(prevPage => prevPage + 1);
            }
        };

        const observerInstance = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0
        });

        const currentRef = lastPostElementRef.current;
        if (currentRef) {
            observerInstance.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observerInstance.unobserve(currentRef);
            }
        };
    }, [loadingPosts, hasMore]);

    useEffect(() => {
        if (page > 1 && bestPost !== null) {
            loadPosts(page, bestPost ? bestPost.id : null);
        }
    }, [page, bestPost]);

    useEffect(() => {
        if (isAuthenticated && waitingCount !== null && !isBanned) {
            setIsReadyToShowButtons(true);
        }
    }, [isAuthenticated, waitingCount, isBanned]);

    const renderMedia = (media_url) => {
        if (!media_url) return null;

        // YouTube
        if (media_url.includes('youtube.com') || media_url.includes('youtu.be')) {
            let embedUrl = '';

            if (media_url.includes('/playlist?list=')) {
                const urlParams = new URLSearchParams(media_url.split('?')[1]);
                const playlistId = urlParams.get('list');
                if (playlistId) {
                    embedUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
                }
            } else {
                let videoId = '';

                if (media_url.includes('/shorts/')) {
                    const path = new URL(media_url).pathname;
                    const parts = path.split('/');
                    const shortsIndex = parts.indexOf('shorts');
                    if (shortsIndex !== -1 && shortsIndex + 1 < parts.length) {
                        const videoIdWithQuery = parts[shortsIndex + 1];
                        videoId = videoIdWithQuery.split('?')[0];
                    }
                } else if (media_url.includes('youtu.be')) {
                    videoId = media_url.split('youtu.be/')[1].split('?')[0];
                } else {
                    const urlParams = new URLSearchParams(media_url.split('?')[1]);
                    videoId = urlParams.get('v');
                }

                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (embedUrl) {
                return (
                    <iframe
                        width="560"
                        height="315"
                        src={embedUrl}
                        title={media_url.includes('/playlist?list=') ? "YouTube playlist" : "YouTube video"}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                return <p>Error while loading video</p>;
            }
        }

        // Twitch
        if (media_url.includes('twitch.tv') && media_url.includes('/clip/')) {
            const parentDomain = 'superpeople.live';
            const clipId = media_url.split('/clip/')[1].split('?')[0];
            const embedUrl = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch clip ${clipId}`}
                ></iframe>
            );
        } else if (media_url.includes('twitch.tv/videos/')) {
            const parentDomain = 'superpeople.live';
            const videoId = media_url.split('videos/')[1].split('?')[0];
            const embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch video ${videoId}`}
                ></iframe>
            );
        }

        // Image
        if (media_url.match(/\.(jpeg|jpg|gif|png)$/i)) {
            return (
                <Zoom>
                    <img
                        src={media_url}
                        alt={t('userProfile.userMedia')}
                        style={{ cursor: 'zoom-in' }}
                    />
                </Zoom>
            );
        }

        // Video
        if (media_url.match(/\.(mp4|webm|ogg)$/i)) {
            return (
                <video controls width="600">
                    <source src={media_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        }
        return (
            <a href={media_url} target="_blank" rel="noopener noreferrer">
                {media_url}
            </a>
        );
    };

    if (loadingPage && posts.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="posts-page">
            <div className="post-header">
                <h1>{t('posts.communityPosts')}</h1>
                <img src="/images/sp-line.png" alt="sp_line" id="line"/>
                {isAuthenticated && !isBanned && isReadyToShowButtons && waitingCount !== null ? (
                    <div className="post-buttons">
                        <button className="create-post-button" onClick={() => navigate('/create-post')}>
                            <FontAwesomeIcon icon={faPlus} />
                            {t('posts.createPost')}
                        </button>
                        {waitingCount > 0 && (
                            <button className="waiting-posts-button" onClick={() => navigate('/my-waiting-posts')}>
                                {t('posts.myWaitingPosts')} ({waitingCount})
                            </button>
                        )}
                    </div>
                ) : !isAuthenticated && loadingPage && (
                    <div className="post-message">
                        <p>{t('posts.authMessage')}</p>
                        <button className="login-button" onClick={handleLogin}>
                            {t('messages.loginViaSteam')}
                        </button>
                    </div>
                )}
            </div>
            {isAuthenticated && isBanned && (
                <p className="ban-message">{t('posts.bannedMessage')}</p>
            )}
            <div className="posts-list">
                {bestPost && (
                    <>
                        <div className="section-title">
                            <span className="title-text">{t('posts.bestPostOfTheWeek')}</span>
                            <span className="arrow-icon">›</span>
                        </div>
                        <div className="post-card best-post no-top-border">
                            {renderPostContent(bestPost)}
                        </div>
                    </>
                )}
                {posts.length > 0 && (
                    <>
                        <div className="section-title">
                            <span className="title-text">{t('header.posts')}</span>
                            <span className="arrow-icon">›</span>
                        </div>
                        <div className="main-posts-wrapper">
                            {posts.map((post, index) => (
                                <div
                                    ref={posts.length === index + 1 ? lastPostElementRef : null}
                                    key={post.id}
                                    className={`post-card ${index === 0 ? 'no-top-border' : ''}`}
                                >
                                    {renderPostContent(post)}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            {loadingPosts && hasMore && <p>{t('posts.loadingMorePosts')}</p>}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('auth.steamLoginTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('auth.steamLoginMessage')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t('auth.cancel')}
                    </Button>
                    <Button variant="primary" onClick={confirmLogin}>
                        {t('auth.proceed')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    function renderPostContent(post) {
        const toggleComments = () => {
            setOpenComments(prev => ({ ...prev, [post.id]: !prev[post.id] }));
        };

        return (
            <>
                <div className="post-content">
                    <div className="post-head">
                        {post.profile_icon ? (
                            <img className="post-avatar" src={post.profile_icon} alt={post.username} />
                        ) : (
                            <span className="post-avatar post-avatar-fallback">
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                        )}
                        <div className="post-author">
                            <Link to={`/user/${post.user_id}`} className="post-username">
                                {post.username}
                            </Link>
                            <div className="post-date">
                                <FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(post.created_at, {format: 'LLL'})}
                            </div>
                        </div>
                    </div>
                    <div className="post-info"
                        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content)}}
                    ></div>
                    <div className="post-media">
                        {renderMedia(post.media_url)}
                    </div>
                </div>
                <div className="post-actions">
                    <span
                        className={`like-button ${likedPosts.has(post.id) ? 'liked' : ''}`}
                        onClick={() => isAuthenticated && handleLike(post.id)}
                        style={{cursor: isAuthenticated ? 'pointer' : 'default' }}
                    >
                        <FontAwesomeIcon icon={faHeart} className="icon" />
                        <span className="like-count">{post.likes}</span>
                    </span>
                    <button className="share-button" onClick={() => handleShare(post.id)}>
                        <FontAwesomeIcon icon={faShareAlt} className="icon" />
                        {t('posts.share')}
                    </button>
                    <button className="comments-button" onClick={toggleComments}>
                        <FontAwesomeIcon icon={faComment} className="icon" />
                        {t('posts.comments')} ({post.comments || 0})
                    </button>
                    {isAdmin && (
                        <button className="delete-post-button" onClick={() => confirmDeletePost(post.id)}>
                            <FontAwesomeIcon icon={faTrash} className="icon" />
                            {t('posts.deletePost')}
                        </button>
                    )}
                </div>
                {openComments[post.id] && (
                    <PostComment
                        postId={post.id}
                        isAuthenticated={isAuthenticated}
                        userId={currentUserId}
                        isAdmin={isAdmin}
                        isBanned={isBanned}
                        token={accessToken}
                        onCommentCountUpdate={(newCount) => onCommentCountUpdate(post.id, newCount)}
                    />
                )}
            </>
        );
    }
}

export default PostsPage;
